/**
 * Detector Stock Recommendations — Table Layout
 * ================================================
 * Compact table-based layout for all tabs.
 * API: /api/recommendations/detector
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Button, Skeleton, alpha, Chip, Tooltip, Tab, Tabs,
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer,
  Paper, IconButton, Alert, Drawer, Divider,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  ShoppingCart as BuyIcon,
  Visibility as WatchIcon,
  AccountBalance as HoldIcon,
  GpsFixed as TargetIcon,
  Shield as StopIcon,
  Timeline as TrailIcon,
  CheckCircle as WinIcon,
  Cancel as LossIcon,
  Star as StarIcon,
  EmojiEvents as TrophyIcon,
  AccessTime as TimeIcon,
  SwapHoriz as SwapIcon,
  Info as InfoIcon,
  Close as CloseIcon,
  Warning as WarningIcon,
  Radar as RadarIcon,
  TrendingUp as TrendingUpIcon,
} from '@mui/icons-material';
import {
  RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  ResponsiveContainer,
} from 'recharts';
import { stockRecommendationsAPI } from '../services/api';

/* Colors */
const C = {
  green: '#10b981', red: '#ef4444', amber: '#f59e0b', blue: '#3b82f6',
  purple: '#8b5cf6', indigo: '#6366f1', cyan: '#06b6d4', slate: '#64748b',
  emerald: '#059669', rose: '#f43f5e', orange: '#f97316',
};

/* Formatters */
const fmtVND = (v) => {
  if (v == null) return '\u2014';
  if (Math.abs(v) >= 1e9) return (v / 1e9).toFixed(2) + 'B';
  if (Math.abs(v) >= 1e6) return (v / 1e6).toFixed(1) + 'M';
  return v.toLocaleString('vi-VN');
};
const fmtPct = (v, d = 2) => v != null ? (v >= 0 ? '+' : '') + v.toFixed(d) + '%' : '\u2014';
const fmtPrice = (v) => v ? v.toLocaleString('vi-VN', { maximumFractionDigits: 0 }) : '\u2014';
const fmtK = (v) => v >= 1e9 ? (v/1e9).toFixed(1) + 'B' : v >= 1e6 ? (v/1e6).toFixed(0) + 'M' : '\u2014';

/* Score level (0-1 → human label) */
const scoreLevel = (v) => {
  const pct = (v || 0) * 100;
  if (pct >= 80) return { text: 'Rất mạnh', color: '#10b981' };
  if (pct >= 65) return { text: 'Mạnh', color: '#3b82f6' };
  if (pct >= 50) return { text: 'Khá', color: '#f59e0b' };
  if (pct >= 35) return { text: 'Trung bình', color: '#f97316' };
  return { text: 'Yếu', color: '#ef4444' };
};

/* Sector label */
const SECTOR_SHORT = {
  ENERGY: 'NL', FINANCIALS: 'TC', INDUSTRIALS: 'CN', MATERIALS: 'VL',
  REAL_ESTATE: 'BĐS', CONSUMER_STAPLES: 'TDTY', CONSUMER_DISCRETIONARY: 'TD',
  INFORMATION_TECHNOLOGY: 'CNTT', HEALTH_CARE: 'YT', UTILITIES: 'TI',
  COMMUNICATION: 'TT',
};
const sectorLabel = (s) => SECTOR_SHORT[s] || s || '\u2014';

/* Exit reason */
const EXIT_CFG = {
  target:   { icon: TargetIcon, color: C.green, label: 'Chốt lời' },
  stop:     { icon: StopIcon, color: C.red, label: 'Cắt lỗ' },
  trailing: { icon: TrailIcon, color: C.blue, label: 'Trailing' },
  time:     { icon: TimeIcon, color: C.amber, label: 'Hết hạn' },
};
const ExitChip = ({ reason }) => {
  const cfg = EXIT_CFG[reason] || { icon: SwapIcon, color: C.slate, label: reason };
  const Icon = cfg.icon;
  return (
    <Chip
      icon={<Icon sx={{ fontSize: 13 }} />}
      label={cfg.label}
      size="small"
      sx={{
        fontWeight: 700, fontSize: '0.65rem', height: 22,
        bgcolor: alpha(cfg.color, 0.1), color: cfg.color,
        border: `1px solid ${alpha(cfg.color, 0.2)}`,
        '& .MuiChip-icon': { color: cfg.color },
      }}
    />
  );
};

/* Quality inline bar */
const QualityBar = ({ value, max = 1, width = 48 }) => {
  const pct = Math.min((value / max) * 100, 100);
  const color = pct >= 80 ? C.green : pct >= 60 ? C.blue : pct >= 40 ? C.amber : C.red;
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, justifyContent: 'flex-end' }}>
      <Box sx={{ width, height: 5, borderRadius: 3, bgcolor: alpha(color, 0.12) }}>
        <Box sx={{ width: `${pct}%`, height: '100%', borderRadius: 3, bgcolor: color }} />
      </Box>
      <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color, minWidth: 22, textAlign: 'right' }}>
        {(value * 100).toFixed(0)}
      </Typography>
    </Box>
  );
};

/* Performance metric — clean stat cell */
const PerfMetric = ({ label, value, color, sub, status, bar }) => (
  <Box sx={{
    textAlign: 'center', py: 1, px: 0.8, position: 'relative',
    borderRight: '1px solid #f1f5f9',
    '&:last-child': { borderRight: 'none' },
  }}>
    <Typography sx={{
      fontSize: '0.7rem', color: '#94a3b8', fontWeight: 600,
      textTransform: 'uppercase', letterSpacing: 0.3, mb: 0.3,
    }}>
      {label}
    </Typography>
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.4 }}>
      {status && (
        <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: status, flexShrink: 0 }} />
      )}
      <Typography sx={{ fontSize: '1rem', fontWeight: 700, color: color || '#1e293b', lineHeight: 1.1, fontFeatureSettings: '"tnum"' }}>
        {value}
      </Typography>
    </Box>
    {sub && (
      <Typography sx={{ fontSize: '0.68rem', color: '#b0b8c4', mt: 0.2, fontWeight: 500, fontFeatureSettings: '"tnum"' }}>
        {sub}
      </Typography>
    )}
    {bar != null && (
      <Box sx={{ mt: 0.4, mx: 'auto', width: '55%', height: 3, borderRadius: 2, bgcolor: alpha(color || '#94a3b8', 0.12) }}>
        <Box sx={{ width: `${Math.min(Math.max(bar, 0), 100)}%`, height: '100%', borderRadius: 2, bgcolor: color }} />
      </Box>
    )}
  </Box>
);

/* SL↔TP progress indicator */
const SLtoTPBar = ({ entry, current, sl, tp }) => {
  if (!entry || !sl || !tp || !current) return null;
  const range = tp - sl;
  if (range <= 0) return null;
  const pct = ((current - sl) / range) * 100;
  const clamped = Math.max(0, Math.min(100, pct));
  const distToSL = ((current - sl) / current) * 100;
  const isNearSL = distToSL < 3; // within 3% of SL
  const isPastEntry = current >= entry;
  const color = isNearSL ? C.red : isPastEntry ? C.green : C.amber;
  return (
    <Tooltip title={`SL ${((current/sl - 1) * 100).toFixed(1)}% | TP ${((tp/current - 1) * 100).toFixed(1)}%`}>
      <Box sx={{ width: 52, position: 'relative' }}>
        <Box sx={{ height: 4, borderRadius: 2, bgcolor: alpha('#94a3b8', 0.12), position: 'relative' }}>
          <Box sx={{ position: 'absolute', left: `${((entry - sl) / range) * 100}%`, top: -1, width: 1, height: 6, bgcolor: '#94a3b8' }} />
          <Box sx={{ width: `${clamped}%`, height: '100%', borderRadius: 2, bgcolor: color, transition: 'width 0.3s' }} />
        </Box>
        {isNearSL && <WarningIcon sx={{ fontSize: 10, color: C.red, position: 'absolute', top: -8, right: -2 }} />}
      </Box>
    </Tooltip>
  );
};

/* Common TH style */
const thSx = {
  fontWeight: 700, fontSize: '0.7rem', color: '#64748b',
  bgcolor: '#f8fafc', py: 1, borderBottom: '2px solid #e2e8f0',
  whiteSpace: 'nowrap',
};



/* Main Component */
export default function DetectorRecommendations() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState(0);
  const [selectedPick, setSelectedPick] = useState(null);
  const [showAllTrades, setShowAllTrades] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await stockRecommendationsAPI.getRecommendations();
      setData(res.data);
    } catch (err) {
      console.error('Error loading recommendations:', err);
      setError('Không thể tải dữ liệu. Vui lòng thử lại.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);



  if (loading) {
    return (
      <Box sx={{ p: 3, maxWidth: 1400, mx: 'auto' }}>
        <Skeleton variant="rounded" width={400} height={36} sx={{ mb: 2 }} />
        <Skeleton variant="rounded" height={48} sx={{ mb: 1 }} />
        {[...Array(5)].map((_, i) => <Skeleton key={i} variant="rounded" height={36} sx={{ mb: 0.5 }} />)}
      </Box>
    );
  }

  const buyPicks = data?.buy_picks || [];
  const holding = (data?.holding || []).sort((a, b) => a.unrealized_pnl_pct - b.unrealized_pnl_pct);
  const track = data?.track_record || {};
  const recentTrades = data?.recent_trades || [];
  const watchlist = data?.watchlist_candidates || [];
  const portfolio = data?.portfolio_summary || {};
  const mCtx = data?.market_context || {};
  const risk = mCtx.risk || {};
  const strategy = mCtx.strategy || {};
  const rotation = mCtx.rotation || {};
  const niche = mCtx.niche || {};
  const sectors = mCtx.sectors || [];

  const streakText = track.current_streak > 0
    ? '\uD83D\uDD25 ' + track.current_streak + ' thắng liên tiếp'
    : track.current_streak < 0
    ? Math.abs(track.current_streak) + ' thua liên tiếp'
    : 'Chuỗi mới';

  /* ── Market context derived ── */
  const alertClr = risk.alert_level === 'CRITICAL' ? C.red : risk.alert_level === 'RED' ? C.rose : risk.alert_level === 'YELLOW' ? C.amber : C.green;
  const sClr = strategy.color === 'red' ? C.red : strategy.color === 'amber' ? C.amber : C.green;
  const flags = risk.active_flags || [];
  const ma20Clr = (niche.pct_above_ma20 || 0) >= 50 ? C.green : (niche.pct_above_ma20 || 0) >= 30 ? C.amber : C.red;
  const breadthClr = (niche.advance_ratio || 0) >= 50 ? C.green : C.red;
  const churnClr = (rotation.leadership_churn || 0) > 40 ? C.amber : C.green;

  return (
    <Box sx={{ p: { xs: 1.5, md: 2.5 }, maxWidth: 1400, mx: 'auto' }}>
      {/* ── Header ── */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2.5, flexWrap: 'wrap', gap: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{
            width: 42, height: 42, borderRadius: 1.5, flexShrink: 0,
            background: `linear-gradient(135deg, ${C.green} 0%, ${C.emerald} 100%)`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: `0 4px 14px ${alpha(C.green, 0.3)}`,
          }}>
            <StarIcon sx={{ fontSize: 22, color: '#fff' }} />
          </Box>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography sx={{ fontSize: '1.35rem', fontWeight: 800, color: '#1e293b', letterSpacing: -0.3 }}>
                Khuyến Nghị Cổ Phiếu
              </Typography>
              {data?.signal_date && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, bgcolor: '#f1f5f9', px: 1, py: 0.3, borderRadius: 1.5 }}>
                  <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: C.green, boxShadow: `0 0 6px ${C.green}` }} />
                  <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#475569' }}>{data.signal_date}</Typography>
                </Box>
              )}
            </Box>
            <Typography sx={{ fontSize: '0.75rem', color: '#94a3b8', fontWeight: 500 }}>
              {`${buyPicks.length} MUA \u00B7 ${holding.length} \u0111ang gi\u1EEF`}
            </Typography>
          </Box>
        </Box>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={loadData}
          disabled={loading}
          sx={{
            textTransform: 'none', fontSize: 13, fontWeight: 600, borderRadius: '4px',
            color: '#64748b', borderColor: '#e2e8f0', px: 2,
            '&:hover': { bgcolor: '#f8fafc', borderColor: '#cbd5e1' },
          }}
        >
          Làm mới
        </Button>
      </Box>

      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2, borderRadius: 2 }}>
          {error}
        </Alert>
      )}

      {/* ═══ MARKET CONTEXT ═══ */}
      {risk.score != null && (
        <Box sx={{ mb: 1.5 }}>
          {/* ── Metrics row ── */}
          <Box sx={{ borderRadius: 2, border: '1px solid #e2e8f0', bgcolor: '#fff', overflow: 'hidden', mb: sectors.length > 0 ? 1 : 0 }}>
            <Box sx={{
              display: 'grid',
              gridTemplateColumns: { xs: 'repeat(3, 1fr)', sm: 'repeat(4, 1fr)', md: 'repeat(7, 1fr)' },
            }}>
              <PerfMetric label={'Rủi ro'} value={risk.score} color={alertClr} sub={risk.alert_level} status={alertClr} />
              <PerfMetric label="Regime" value={risk.regime || '—'} color={risk.regime === 'RISK_OFF' ? C.red : risk.regime === 'RISK_ON' ? C.green : C.amber} sub={risk.highvol_regime ? risk.highvol_regime.replace('_', ' ') : '—'} status={risk.regime === 'RISK_OFF' ? C.red : C.green} />
              <PerfMetric label={'> MA20'} value={`${niche.pct_above_ma20 || 0}%`} color={ma20Clr} sub={(niche.pct_above_ma20 || 0) < 30 ? 'thấp' : (niche.pct_above_ma20 || 0) < 50 ? 'trung bình' : 'mạnh'} status={ma20Clr} />
              <PerfMetric label={'Độ rộng'} value={`${niche.advance_ratio || 0}%`} color={breadthClr} sub={(niche.advance_ratio || 0) < 40 ? 'hẹp' : 'rộng'} status={breadthClr} />
              <PerfMetric label="Churn" value={`${rotation.leadership_churn || 0}%`} color={churnClr} sub={(rotation.leadership_churn || 0) > 40 ? 'xoay mạnh' : 'ổn định'} status={churnClr} />
              <PerfMetric label={'Chiến lược'} value={strategy.mode || '—'} color={sClr} sub={strategy.mode ? `Cash ${strategy.cash_pct}` : null} status={sClr} />
              <PerfMetric label={'Cảnh báo'} value={flags.length > 0 ? flags.length : '✓'} color={flags.length > 0 ? C.red : C.green} sub={flags.length > 0 ? flags.slice(0, 2).map(f => f.replace(/_/g, ' ')).join(', ') : 'an toàn'} status={flags.length > 0 ? C.red : C.green} />
            </Box>

            {/* ── Footer: strategy + flags ── */}
            {(strategy.mode || flags.length > 0) && (
              <Box sx={{ borderTop: '1px solid #f1f5f9', px: 1.5, py: 0.6, display: 'flex', alignItems: 'center', gap: 0.8, flexWrap: 'wrap' }}>
                {strategy.mode && (
                  <Typography component="span" sx={{ fontSize: '0.75rem', color: '#64748b', fontWeight: 500 }}>
                    <span style={{ color: sClr, fontWeight: 700 }}>{strategy.mode}</span>
                    {' · Cash '}<b>{strategy.cash_pct}</b>
                    {' · Active '}<b>{strategy.active_pct}</b>
                    {' · Max '}<b>{strategy.max_positions}</b>
                  </Typography>
                )}
                {rotation.is_rotation && (
                  <Chip label="ROTATION" size="small" sx={{
                    bgcolor: alpha(C.purple, 0.06), color: C.purple, fontWeight: 700, fontSize: '0.65rem', height: 20,
                    '& .MuiChip-label': { px: 0.5 },
                  }} />
                )}
                {niche.is_niche && (
                  <Chip label={`NICHE ${niche.score}/4`} size="small" sx={{
                    bgcolor: alpha(C.indigo, 0.06), color: C.indigo, fontWeight: 700, fontSize: '0.65rem', height: 20,
                    '& .MuiChip-label': { px: 0.5 },
                  }} />
                )}
                {flags.length > 0 && (
                  <Typography component="span" sx={{ fontSize: '0.72rem', color: C.red, fontWeight: 600 }}>
                    {'⚠ '}{flags.map(f => f.replace(/_/g, ' ')).join(', ')}
                  </Typography>
                )}
              </Box>
            )}

            {/* ── Strategy advice banner ── */}
            {strategy.advice && (
              <Box sx={{
                bgcolor: alpha(sClr, 0.06),
                borderTop: `2px solid ${alpha(sClr, 0.3)}`,
                px: 1.5, py: 0.8,
                display: 'flex', alignItems: 'flex-start', gap: 0.8,
              }}>
                <Box sx={{
                  width: 20, height: 20, borderRadius: '50%', bgcolor: alpha(sClr, 0.12),
                  display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, mt: 0.1,
                }}>
                  {strategy.color === 'red' ? <StopIcon sx={{ fontSize: 13, color: sClr }} />
                  : strategy.color === 'amber' ? <WarningIcon sx={{ fontSize: 13, color: sClr }} />
                  : <TrendingUpIcon sx={{ fontSize: 13, color: sClr }} />}
                </Box>
                <Typography sx={{ fontSize: '0.78rem', color: '#334155', fontWeight: 500, lineHeight: 1.5 }}>
                  {strategy.advice}
                </Typography>
              </Box>
            )}
          </Box>

          {/* ── Sector table ── */}
          {sectors.length > 0 && (
            <Box sx={{ borderRadius: 2, border: '1px solid #e2e8f0', bgcolor: '#fff', overflow: 'hidden' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, px: 1.5, py: 0.6, borderBottom: '1px solid #f1f5f9' }}>
                <RadarIcon sx={{ fontSize: 16, color: C.blue }} />
                <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: '#334155' }}>{'Ngành'}</Typography>
                {(rotation.top_sectors || []).map(s => (
                  <Chip key={s} label={sectorLabel(s)} size="small" sx={{
                    fontWeight: 700, fontSize: '0.68rem', height: 22, bgcolor: alpha(C.green, 0.08), color: C.green,
                    border: `1px solid ${alpha(C.green, 0.15)}`, '& .MuiChip-label': { px: 0.6 },
                  }} />
                ))}
                <Box sx={{ flex: 1 }} />
                {rotation.is_rotation && (
                  <Chip label="ROTATION" size="small" sx={{
                    bgcolor: alpha(C.purple, 0.08), color: C.purple, fontWeight: 700, fontSize: '0.68rem', height: 22,
                    border: `1px solid ${alpha(C.purple, 0.2)}`, '& .MuiChip-label': { px: 0.6 },
                  }} />
                )}
              </Box>
              <Table size="small" sx={{ tableLayout: 'fixed', width: '100%' }}>
                <colgroup>
                  <col style={{ width: '25%' }} />
                  <col style={{ width: '14%' }} />
                  <col style={{ width: '20%' }} />
                  <col style={{ width: '20%' }} />
                  <col style={{ width: '21%' }} />
                </colgroup>
                <TableHead>
                  <TableRow sx={{ '& th': { fontSize: '0.72rem', fontWeight: 600, color: '#94a3b8', py: 0.5, borderBottom: '1px solid #f1f5f9' } }}>
                    <TableCell sx={{ pl: 1.5 }}>{'Ngành'}</TableCell>
                    <TableCell align="center">{'Trạng thái'}</TableCell>
                    <TableCell>{'Cơ hội'}</TableCell>
                    <TableCell>{'Rủi ro'}</TableCell>
                    <TableCell align="center" sx={{ pr: 1.2 }}>{'Đánh giá'}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sectors.slice(0, 10).map((sec) => {
                    const isTop = (rotation.top_sectors || []).includes(sec.code);
                    const qLabel = sec.quadrant === 'A' ? 'Dẫn dắt' : sec.quadrant === 'B' ? 'Cải thiện' : sec.quadrant === 'C' ? 'Trung lập' : 'Suy yếu';
                    const qColor = sec.quadrant === 'A' ? C.green : sec.quadrant === 'B' ? C.blue : sec.quadrant === 'C' ? C.amber : C.red;
                    const verdict = sec.priority > 0 ? 'Nên xem' : sec.priority > -20 ? 'Chờ' : 'Tránh';
                    const verdictClr = sec.priority > 0 ? C.green : sec.priority > -20 ? C.amber : C.red;
                    return (
                      <TableRow key={sec.code} sx={{
                        ...(isTop && {
                          bgcolor: alpha(C.green, 0.04),
                          borderLeft: `3px solid ${C.green}`,
                        }),
                        '& td': { py: 0.7, borderBottom: '1px solid #f1f5f9', fontSize: '0.78rem' },
                      }}>
                        <TableCell sx={{ pl: isTop ? 1 : 1.5, fontWeight: isTop ? 700 : 500, color: isTop ? C.green : '#475569' }}>
                          {isTop && (
                            <Chip label="DẪN DẮT" size="small" sx={{
                              bgcolor: alpha(C.green, 0.12), color: C.green, fontWeight: 700,
                              fontSize: '0.55rem', height: 16, mr: 0.5, verticalAlign: 'middle',
                              '& .MuiChip-label': { px: 0.4 },
                            }} />
                          )}
                          {sec.name}
                          <Typography sx={{ fontSize: '0.62rem', color: '#94a3b8', fontWeight: 400 }}>
                            {sec.stocks} cổ phiếu
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Chip label={qLabel} size="small" sx={{
                            bgcolor: alpha(qColor, 0.08), color: qColor, fontWeight: 700,
                            fontSize: '0.62rem', height: 20,
                            '& .MuiChip-label': { px: 0.5 },
                          }} />
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Box sx={{ flex: 1, height: 6, borderRadius: 3, bgcolor: '#f1f5f9', overflow: 'hidden' }}>
                              <Box sx={{ width: `${Math.min(sec.opportunity, 100)}%`, height: '100%', borderRadius: 3, bgcolor: sec.opportunity > 30 ? C.green : '#cbd5e1' }} />
                            </Box>
                            <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: sec.opportunity > 30 ? C.green : '#94a3b8', minWidth: 28, textAlign: 'right' }}>
                              {sec.opportunity}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Box sx={{ flex: 1, height: 6, borderRadius: 3, bgcolor: '#f1f5f9', overflow: 'hidden' }}>
                              <Box sx={{ width: `${Math.min(sec.risk, 100)}%`, height: '100%', borderRadius: 3, bgcolor: sec.risk > 50 ? C.red : '#cbd5e1' }} />
                            </Box>
                            <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: sec.risk > 50 ? C.red : '#94a3b8', minWidth: 28, textAlign: 'right' }}>
                              {sec.risk}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell align="center">
                          <Chip label={verdict} size="small" sx={{
                            bgcolor: alpha(verdictClr, 0.08), color: verdictClr, fontWeight: 700,
                            fontSize: '0.66rem', height: 22,
                            border: `1px solid ${alpha(verdictClr, 0.15)}`,
                            '& .MuiChip-label': { px: 0.6 },
                          }} />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Box>
          )}
        </Box>
      )}

      {/* Track Record Banner */}
      <Box sx={{ borderRadius: 2, border: '1px solid #e2e8f0', bgcolor: '#fff', mb: 1.5, overflow: 'hidden' }}>
        {/* Inline title row */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 1.5, pt: 1, pb: 0.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
            <TrophyIcon sx={{ fontSize: 15, color: C.amber }} />
            <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: '#334155' }}>
              Hiệu suất hệ thống
            </Typography>
          </Box>
          <Chip
            label={streakText}
            size="small"
            sx={{
              bgcolor: alpha(track.current_streak > 0 ? C.green : C.red, 0.08),
              color: track.current_streak > 0 ? C.green : track.current_streak < 0 ? C.red : '#94a3b8',
              fontWeight: 700, fontSize: '0.58rem', height: 20,
              border: `1px solid ${alpha(track.current_streak > 0 ? C.green : C.red, 0.2)}`,
              '& .MuiChip-label': { px: 0.6 },
            }}
          />
        </Box>

        {/* Metrics grid */}
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: { xs: 'repeat(4, 1fr)', md: 'repeat(8, 1fr)' },
        }}>
          <PerfMetric
            label="Tổng LN"
            value={fmtPct(track.total_return_pct)}
            color={track.total_return_pct >= 0 ? C.green : C.red}
            sub={fmtVND(track.total_pnl_vnd)}
            status={track.total_return_pct > 0 ? C.green : C.red}
          />
          <PerfMetric
            label="Sharpe"
            value={track.sharpe_ratio?.toFixed(2) || '\u2014'}
            color={track.sharpe_ratio >= 2 ? C.green : track.sharpe_ratio >= 1 ? C.blue : C.amber}
            status={track.sharpe_ratio >= 1.5 ? C.green : track.sharpe_ratio >= 1 ? C.amber : C.red}
          />
          <PerfMetric
            label="Win Rate"
            value={`${track.win_rate_pct || 0}%`}
            color={track.win_rate_pct >= 55 ? C.green : track.win_rate_pct >= 45 ? C.amber : C.red}
            sub={`${track.wins || 0}W / ${track.losses || 0}L`}
            status={track.win_rate_pct >= 50 ? C.green : C.red}
            bar={track.win_rate_pct || 0}
          />
          <PerfMetric
            label="TB Thắng"
            value={fmtPct(track.avg_win_pct)}
            color={C.green}
            status={C.green}
          />
          <PerfMetric
            label="TB Thua"
            value={fmtPct(track.avg_loss_pct)}
            color={C.red}
            status={C.red}
          />
          <PerfMetric
            label="Profit Factor"
            value={track.profit_factor?.toFixed(2) || '\u2014'}
            color={track.profit_factor >= 2 ? C.green : track.profit_factor >= 1 ? C.blue : C.red}
            status={track.profit_factor >= 1.5 ? C.green : track.profit_factor >= 1 ? C.amber : C.red}
            bar={Math.min((track.profit_factor || 0) / 3 * 100, 100)}
          />
          <PerfMetric
            label="Max DD"
            value={fmtPct(track.max_drawdown_pct)}
            color={C.red}
            status={Math.abs(track.max_drawdown_pct || 0) < 10 ? C.green : Math.abs(track.max_drawdown_pct || 0) < 15 ? C.amber : C.red}
          />
          <PerfMetric
            label="Ngày TB"
            value={track.avg_days_held?.toFixed(1) || '\u2014'}
            color={C.slate}
          />
        </Box>
      </Box>

      {/* Tabs */}
      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        sx={{
          mb: 2, minHeight: 38,
          '& .MuiTab-root': {
            fontWeight: 700, fontSize: '0.78rem', textTransform: 'none',
            minHeight: 38, borderRadius: 1.5, mx: 0.3, py: 0,
          },
          '& .Mui-selected': { color: C.green },
          '& .MuiTabs-indicator': { backgroundColor: C.green, height: 2.5, borderRadius: 1.5 },
        }}
      >
        <Tab icon={<BuyIcon sx={{ fontSize: 16 }} />} iconPosition="start" label={`MUA (${buyPicks.length})`} />
        <Tab icon={<HoldIcon sx={{ fontSize: 16 }} />} iconPosition="start" label={`\u0110ang gi\u1EEF (${holding.length})`} />
        <Tab icon={<WatchIcon sx={{ fontSize: 16 }} />} iconPosition="start" label={`Theo d\u00F5i (${watchlist.length})`} />
        <Tab icon={<TimeIcon sx={{ fontSize: 16 }} />} iconPosition="start" label="Giao dịch" />
      </Tabs>

      {/* TAB 0: BUY Picks */}
      {tab === 0 && (
        <Box>
          {buyPicks.length === 0 ? (
            <Paper sx={{ p: 3, textAlign: 'center', borderRadius: 2, border: '1px dashed #cbd5e1' }}>
              <Typography sx={{ color: '#94a3b8', fontSize: '0.85rem' }}>Không có khuyến nghị MUA hôm nay</Typography>
            </Paper>
          ) : (
            <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 2.5, border: '1px solid #e2e8f0' }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ '& th': thSx }}>
                    <TableCell>Hạng</TableCell>
                    <TableCell>Mã CK</TableCell>
                    <TableCell align="right">Giá vào</TableCell>
                    <TableCell align="right" sx={{ color: C.red + ' !important' }}>Cắt lỗ</TableCell>
                    <TableCell align="right" sx={{ color: C.emerald + ' !important' }}>Chốt lời</TableCell>
                    <TableCell align="right">R:R</TableCell>
                    <TableCell align="right">Quality</TableCell>
                    <TableCell align="right">Win%</TableCell>
                    <TableCell>Ngành</TableCell>
                    <TableCell>Setup</TableCell>
                    <TableCell align="right">GTGD</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {buyPicks.map((pick, i) => {
                    const prob = pick.probability || {};
                    const confColor = prob.confidence === 'HIGH' ? C.green : prob.confidence === 'MEDIUM' ? C.amber : C.slate;
                    return (
                        <TableRow
                          key={pick.ticker}
                          hover
                          sx={{
                            cursor: 'pointer',
                            '&:hover': { bgcolor: alpha(C.green, 0.03) },
                          }}
                          onClick={() => setSelectedPick(pick)}
                        >
                          <TableCell sx={{ fontWeight: 600, color: '#94a3b8', fontSize: '0.75rem' }}>
                            {i + 1}
                          </TableCell>
                          <TableCell>
                            <Box>
                              <Typography sx={{ fontWeight: 800, fontSize: '0.88rem', color: '#0f172a', lineHeight: 1.2 }}>
                                {pick.ticker}
                              </Typography>
                              <Typography sx={{ fontSize: '0.6rem', color: '#94a3b8' }}>
                                {pick.company_name}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.82rem', color: '#0f172a' }}>
                            {fmtPrice(pick.entry_price)}
                          </TableCell>
                          <TableCell align="right">
                            <Tooltip title={`${pick.stop_loss_pct}%`}>
                              <Typography sx={{ fontWeight: 700, fontSize: '0.8rem', color: C.red }}>
                                {fmtPrice(pick.stop_loss_price)}
                              </Typography>
                            </Tooltip>
                          </TableCell>
                          <TableCell align="right">
                            <Tooltip title={`${pick.take_profit_pct}%`}>
                              <Typography sx={{ fontWeight: 700, fontSize: '0.8rem', color: C.emerald }}>
                                {fmtPrice(pick.target_price)}
                              </Typography>
                            </Tooltip>
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.78rem', color: C.blue }}>
                            {pick.risk_reward}
                          </TableCell>
                          <TableCell align="right">
                            <QualityBar value={pick.quality_score} />
                          </TableCell>
                          <TableCell align="right">
                            <Tooltip title={`10d: ${prob.win_10d}% \u00B7 Edge: ${prob.edge}%`}>
                              <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                                <Typography sx={{ fontWeight: 700, fontSize: '0.78rem', color: confColor }}>
                                  {prob.win_5d != null ? prob.win_5d + '%' : '\u2014'}
                                </Typography>
                                <Chip
                                  label={prob.confidence === 'HIGH' ? 'H' : prob.confidence === 'MEDIUM' ? 'M' : 'L'}
                                  size="small"
                                  sx={{
                                    fontSize: '0.55rem', fontWeight: 800, height: 16, minWidth: 16,
                                    bgcolor: alpha(confColor, 0.1), color: confColor,
                                    '& .MuiChip-label': { px: 0.4 },
                                  }}
                                />
                              </Box>
                            </Tooltip>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={sectorLabel(pick.sector)}
                              size="small"
                              sx={{ fontSize: '0.62rem', height: 20, bgcolor: alpha(C.indigo, 0.07), color: C.indigo, fontWeight: 700 }}
                            />
                          </TableCell>
                          <TableCell sx={{ fontSize: '0.7rem', color: '#64748b' }}>{pick.setup_type}</TableCell>
                          <TableCell align="right" sx={{ fontSize: '0.7rem', color: '#94a3b8' }}>
                            {fmtK(pick.value_traded)}
                          </TableCell>
                        </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {/* Disclaimer */}
          <Box sx={{
            mt: 2, px: 2, py: 1.2, borderRadius: 2,
            bgcolor: alpha(C.amber, 0.04),
            border: `1px solid ${alpha(C.amber, 0.15)}`,
            display: 'flex', gap: 1,
          }}>
            <InfoIcon sx={{ fontSize: 14, color: C.amber, mt: 0.2 }} />
            <Typography sx={{ fontSize: '0.65rem', color: '#64748b', lineHeight: 1.5 }}>
              <b style={{ color: C.amber }}>LƯU Ý:</b>{' Khuyến nghị AI dựa trên 7 module phân tích kỹ thuật. Win rate: '}
              <b>{track.win_rate_pct}%</b>{' \u00B7 PF: '}<b>{track.profit_factor}</b>
              {'. Luôn tuân thủ cắt lỗ, không đầu tư quá 20% danh mục vào một cổ phiếu.'}
            </Typography>
          </Box>
        </Box>
      )}

      {/* TAB 1: Holding */}
      {tab === 1 && (
        <Box>
          {holding.length === 0 ? (
            <Paper sx={{ p: 3, textAlign: 'center', borderRadius: 2, border: '1px dashed #cbd5e1' }}>
              <Typography sx={{ color: '#94a3b8' }}>Không có vị thế đang mở</Typography>
            </Paper>
          ) : (
            <>
              {/* Portfolio + Position Summary */}
              <Paper elevation={0} sx={{
                display: 'flex', justifyContent: 'space-around', mb: 1.5, p: 1,
                borderRadius: 2, border: '1px solid #e2e8f0', bgcolor: '#f8fafc', flexWrap: 'wrap', gap: 0.5,
              }}>
                {[
                  ['Tài khoản', fmtVND(portfolio.total_equity), C.blue],
                  ['Tiền mặt', fmtVND(portfolio.cash_balance), portfolio.cash_pct < 5 ? C.red : C.slate,
                    `${portfolio.cash_pct || 0}%`],
                  ['Vị thế', `${holding.length}/15`, holding.length >= 15 ? C.amber : '#1e293b',
                    `${portfolio.exposure_pct || 0}% exposure`],
                  ['Đang lãi', holding.filter(h => h.unrealized_pnl_pct > 0).length, C.green],
                  ['Đang lỗ', holding.filter(h => h.unrealized_pnl_pct <= 0).length, C.red],
                  ['Tổng P&L', fmtVND(holding.reduce((s, h) => s + h.unrealized_pnl_vnd, 0)),
                    holding.reduce((s, h) => s + h.unrealized_pnl_vnd, 0) >= 0 ? C.green : C.red],
                  ['DD hiện tại', fmtPct(portfolio.drawdown_pct), Math.abs(portfolio.drawdown_pct || 0) > 5 ? C.red : C.amber],
                ].map(([label, val, color, sub]) => (
                  <Box key={label} sx={{ textAlign: 'center', minWidth: 70 }}>
                    <Typography sx={{ fontSize: '0.58rem', color: '#94a3b8', fontWeight: 600 }}>{label}</Typography>
                    <Typography sx={{ fontWeight: 800, fontSize: '0.85rem', color }}>{val}</Typography>
                    {sub && <Typography sx={{ fontSize: '0.55rem', color: '#94a3b8' }}>{sub}</Typography>}
                  </Box>
                ))}
              </Paper>

              {/* Near-SL warning */}
              {holding.some(h => h.stop_loss_price && h.current_price && ((h.current_price - h.stop_loss_price) / h.current_price * 100) < 3) && (
                <Alert severity="warning" icon={<WarningIcon />} sx={{ mb: 1, borderRadius: 2, fontSize: '0.72rem', py: 0.5 }}>
                  <b>Cảnh báo:</b> Có vị thế gần mức cắt lỗ! Kiểm tra ngay.
                </Alert>
              )}

              <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 2.5, border: '1px solid #e2e8f0' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ '& th': thSx }}>
                      <TableCell>Mã CK</TableCell>
                      <TableCell>Ngành</TableCell>
                      <TableCell align="right">Giá vào</TableCell>
                      <TableCell align="right">Giá HT</TableCell>
                      <TableCell align="right">P&L %</TableCell>
                      <TableCell align="right">P&L VND</TableCell>
                      <TableCell align="right" sx={{ color: C.red + ' !important' }}>SL</TableCell>
                      <TableCell align="right" sx={{ color: C.emerald + ' !important' }}>TP</TableCell>
                      <TableCell align="center">SL↔TP</TableCell>
                      <TableCell align="right">Quality</TableCell>
                      <TableCell align="right">Ngày</TableCell>
                      <TableCell>Setup</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {holding.map((pos) => {
                      const isWin = pos.unrealized_pnl_pct > 0;
                      const pnlColor = isWin ? C.green : C.red;
                      const distToSL = pos.stop_loss_price && pos.current_price
                        ? ((pos.current_price - pos.stop_loss_price) / pos.current_price * 100) : 999;
                      const isNearSL = distToSL < 3;
                      return (
                        <TableRow key={pos.ticker} hover sx={{
                          '&:hover': { bgcolor: alpha(pnlColor, 0.02) },
                          ...(isNearSL && { bgcolor: alpha(C.red, 0.04), '&:hover': { bgcolor: alpha(C.red, 0.07) } }),
                        }}>
                          <TableCell>
                            <Box>
                              <Typography sx={{ fontWeight: 800, fontSize: '0.85rem', color: '#0f172a', lineHeight: 1.2 }}>
                                {pos.ticker}
                              </Typography>
                              <Typography sx={{ fontSize: '0.58rem', color: '#94a3b8' }}>
                                {pos.company_name}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={sectorLabel(pos.sector)}
                              size="small"
                              sx={{ fontSize: '0.6rem', height: 18, bgcolor: alpha(C.indigo, 0.06), color: C.indigo, fontWeight: 600 }}
                            />
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.8rem' }}>
                            {fmtPrice(pos.entry_price)}
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.8rem', color: pnlColor }}>
                            {fmtPrice(pos.current_price)}
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 800, fontSize: '0.82rem', color: pnlColor }}>
                            {fmtPct(pos.unrealized_pnl_pct)}
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.76rem', color: pnlColor }}>
                            {fmtVND(pos.unrealized_pnl_vnd)}
                          </TableCell>
                          <TableCell align="right" sx={{ fontSize: '0.75rem', color: C.red }}>
                            <Tooltip title={pos.stop_loss_pct ? `-${pos.stop_loss_pct}%` : ''}>
                              <span>{fmtPrice(pos.stop_loss_price)}</span>
                            </Tooltip>
                          </TableCell>
                          <TableCell align="right" sx={{ fontSize: '0.75rem', color: C.emerald }}>
                            <Tooltip title={pos.take_profit_pct ? `+${pos.take_profit_pct}%` : ''}>
                              <span>{fmtPrice(pos.target_price)}</span>
                            </Tooltip>
                          </TableCell>
                          <TableCell align="center">
                            <SLtoTPBar
                              entry={pos.entry_price}
                              current={pos.current_price}
                              sl={pos.stop_loss_price}
                              tp={pos.target_price}
                            />
                          </TableCell>
                          <TableCell align="right">
                            {pos.quality_score != null && <QualityBar value={pos.quality_score} />}
                          </TableCell>
                          <TableCell align="right" sx={{ fontSize: '0.75rem', color: '#64748b' }}>
                            {pos.days_held}d
                          </TableCell>
                          <TableCell sx={{ fontSize: '0.68rem', color: '#64748b' }}>
                            {pos.setup_type}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </Box>
      )}

      {/* TAB 2: Watchlist */}
      {tab === 2 && (
        <Box>
          {watchlist.length === 0 ? (
            <Paper sx={{ p: 3, textAlign: 'center', borderRadius: 2, border: '1px dashed #cbd5e1' }}>
              <Typography sx={{ color: '#94a3b8' }}>Không có ứng viên theo dõi</Typography>
            </Paper>
          ) : (
            <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 2.5, border: '1px solid #e2e8f0' }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ '& th': thSx }}>
                    <TableCell>Hạng</TableCell>
                    <TableCell>Mã CK</TableCell>
                    <TableCell align="right">Giá vào</TableCell>
                    <TableCell align="right" sx={{ color: C.red + ' !important' }}>Cắt lỗ</TableCell>
                    <TableCell align="right" sx={{ color: C.emerald + ' !important' }}>Chốt lời</TableCell>
                    <TableCell align="right">R:R</TableCell>
                    <TableCell align="right">Quality</TableCell>
                    <TableCell align="right">Win%</TableCell>
                    <TableCell>Ngành</TableCell>
                    <TableCell>Setup</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {watchlist.map((w, i) => {
                    const wp = w.probability || {};
                    return (
                    <TableRow key={w.ticker} hover sx={{ '&:hover': { bgcolor: alpha(C.amber, 0.03) } }}>
                      <TableCell sx={{ fontWeight: 600, color: '#94a3b8', fontSize: '0.75rem' }}>{i + 1}</TableCell>
                      <TableCell>
                        <Box>
                          <Typography sx={{ fontWeight: 800, fontSize: '0.85rem', color: '#1e293b', lineHeight: 1.2 }}>{w.ticker}</Typography>
                          <Typography sx={{ fontSize: '0.58rem', color: '#94a3b8' }}>
                            {w.company_name}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.8rem' }}>{fmtPrice(w.entry_price)}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.78rem', color: C.red }}>{fmtPrice(w.stop_loss_price)}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.78rem', color: C.emerald }}>{fmtPrice(w.target_price)}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.78rem', color: C.blue }}>{w.risk_reward}</TableCell>
                      <TableCell align="right"><QualityBar value={w.quality_score} /></TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.78rem', color: wp.win_5d >= 50 ? C.green : C.slate }}>
                        {wp.win_5d != null ? wp.win_5d + '%' : '\u2014'}
                      </TableCell>
                      <TableCell>
                        <Chip label={sectorLabel(w.sector)} size="small"
                          sx={{ fontSize: '0.6rem', height: 18, bgcolor: alpha(C.indigo, 0.06), color: C.indigo, fontWeight: 600 }} />
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.7rem', color: '#64748b' }}>{w.setup_type}</TableCell>
                    </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      )}

      {/* TAB 3: Recent Trades */}
      {tab === 3 && (
        <Box>
          {/* Trade Summary */}
          {recentTrades.length > 0 && (
            <Paper elevation={0} sx={{
              display: 'flex', justifyContent: 'space-around', mb: 1.5, p: 1,
              borderRadius: 2, border: '1px solid #e2e8f0', bgcolor: '#f8fafc', flexWrap: 'wrap', gap: 0.5,
            }}>
              {(() => {
                const wins = recentTrades.filter(t => t.is_win);
                const losses = recentTrades.filter(t => !t.is_win);
                const best = recentTrades.reduce((a, b) => (a.pnl_pct || 0) > (b.pnl_pct || 0) ? a : b, recentTrades[0]);
                const worst = recentTrades.reduce((a, b) => (a.pnl_pct || 0) < (b.pnl_pct || 0) ? a : b, recentTrades[0]);
                return [
                  [`${recentTrades.length} giao d\u1ecbch`, `${wins.length}W / ${losses.length}L`, wins.length > losses.length ? C.green : C.red],
                  ['TB L\u00e3i', fmtPct(wins.length ? wins.reduce((s, t) => s + t.pnl_pct, 0) / wins.length : 0), C.green],
                  ['TB L\u1ed7', fmtPct(losses.length ? losses.reduce((s, t) => s + t.pnl_pct, 0) / losses.length : 0), C.red],
                  ['Best', `${best.ticker} ${fmtPct(best.pnl_pct)}`, C.green],
                  ['Worst', `${worst.ticker} ${fmtPct(worst.pnl_pct)}`, C.red],
                ].map(([label, val, color]) => (
                  <Box key={label} sx={{ textAlign: 'center', minWidth: 80 }}>
                    <Typography sx={{ fontSize: '0.58rem', color: '#94a3b8', fontWeight: 600 }}>{label}</Typography>
                    <Typography sx={{ fontWeight: 800, fontSize: '0.8rem', color }}>{val}</Typography>
                  </Box>
                ));
              })()}
            </Paper>
          )}

          <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 2.5, border: '1px solid #e2e8f0' }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ '& th': thSx }}>
                <TableCell sx={{ width: 32 }} />
                <TableCell>M\u00e3 CK</TableCell>
                <TableCell>Mua</TableCell>
                <TableCell>B\u00e1n</TableCell>
                <TableCell align="right">Gi\u00e1 v\u00e0o</TableCell>
                <TableCell align="right">Gi\u00e1 ra</TableCell>
                <TableCell align="right">P&L %</TableCell>
                <TableCell align="right">P&L VND</TableCell>
                <TableCell>L\u00fd do</TableCell>
                <TableCell>Ng\u00e0nh</TableCell>
                <TableCell align="right">Ng\u00e0y</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {recentTrades.length === 0 && (
                <TableRow>
                  <TableCell colSpan={11} sx={{ textAlign: 'center', py: 3, color: '#94a3b8' }}>
                    Ch\u01b0a c\u00f3 giao d\u1ecbch
                  </TableCell>
                </TableRow>
              )}
              {(showAllTrades ? recentTrades : recentTrades.slice(0, 10)).map((t, i) => (
                <TableRow key={i} hover sx={{ '&:hover': { bgcolor: alpha(t.is_win ? C.green : C.red, 0.02) } }}>
                  <TableCell sx={{ pr: 0 }}>
                    {t.is_win
                      ? <WinIcon sx={{ fontSize: 18, color: C.green }} />
                      : <LossIcon sx={{ fontSize: 18, color: C.red }} />}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 800, fontSize: '0.82rem', color: '#1e293b' }}>{t.ticker}</TableCell>
                  <TableCell sx={{ fontSize: '0.7rem', color: '#64748b' }}>{t.entry_date}</TableCell>
                  <TableCell sx={{ fontSize: '0.7rem', color: '#64748b' }}>{t.exit_date}</TableCell>
                  <TableCell align="right" sx={{ fontSize: '0.78rem' }}>{fmtPrice(t.entry_price)}</TableCell>
                  <TableCell align="right" sx={{ fontSize: '0.78rem' }}>{fmtPrice(t.exit_price)}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 800, fontSize: '0.82rem', color: t.is_win ? C.green : C.red }}>
                    {fmtPct(t.pnl_pct)}
                  </TableCell>
                  <TableCell align="right" sx={{ fontSize: '0.74rem', fontWeight: 600, color: t.is_win ? C.green : C.red }}>
                    {fmtVND(t.pnl_amount)}
                  </TableCell>
                  <TableCell><ExitChip reason={t.exit_reason} /></TableCell>
                  <TableCell>
                    <Chip label={sectorLabel(t.sector)} size="small"
                      sx={{ fontSize: '0.55rem', height: 16, bgcolor: alpha(C.indigo, 0.06), color: C.indigo, fontWeight: 600 }} />
                  </TableCell>
                  <TableCell align="right" sx={{ fontSize: '0.74rem', color: '#64748b' }}>{t.days_held}d</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

          {recentTrades.length > 10 && (
            <Box sx={{ textAlign: 'center', mt: 1 }}>
              <Button
                size="small"
                onClick={() => setShowAllTrades(!showAllTrades)}
                sx={{ fontSize: '0.72rem', color: C.blue, textTransform: 'none' }}
              >
                {showAllTrades ? `Thu g\u1ecdn (10/${recentTrades.length})` : `Xem t\u1ea5t c\u1ea3 ${recentTrades.length} giao d\u1ecbch`}
              </Button>
            </Box>
          )}
        </Box>
      )}

      {/* ── Detail Drawer ── */}
      <Drawer
        anchor="right"
        open={!!selectedPick}
        onClose={() => setSelectedPick(null)}
        slotProps={{ paper: { sx: { width: { xs: '100%', sm: 420 }, p: 0 } } }}
      >
        {selectedPick && (() => {
          const pick = selectedPick;
          const scores = pick.scores || {};
          const prob = pick.probability || {};
          const radarData = [
            { subject: 'Xu hướng', value: scores.trend || 0 },
            { subject: 'Sức mạnh', value: scores.relative_strength || 0 },
            { subject: 'Thanh khoản', value: scores.volume || 0 },
            { subject: 'An toàn', value: scores.anti_trap || 0 },
            { subject: 'Ngành', value: scores.sector || 0 },
            { subject: 'Phá vỡ', value: scores.breakout || 0 },
          ];
          const confColor = prob.confidence === 'HIGH' ? C.green : prob.confidence === 'MEDIUM' ? C.amber : C.slate;
          return (
            <>
              {/* Header */}
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                px: 2.5, py: 1.5, borderBottom: '1px solid #e2e8f0', bgcolor: '#f8fafc' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <StarIcon sx={{ fontSize: 20, color: C.green }} />
                  <Box>
                    <Typography sx={{ fontSize: '1rem', fontWeight: 800, color: '#0f172a' }}>{pick.ticker}</Typography>
                    {pick.company_name && (
                      <Typography sx={{ fontSize: '0.72rem', color: '#64748b' }}>{pick.company_name}</Typography>
                    )}
                  </Box>
                </Box>
                <IconButton size="small" onClick={() => setSelectedPick(null)}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>

              <Box sx={{ p: 2.5, overflowY: 'auto', flex: 1 }}>
                {/* Setup + Sector */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <Chip label={pick.setup_type} size="small" sx={{ fontWeight: 700, fontSize: '0.7rem', height: 24, bgcolor: alpha(C.green, 0.08), color: C.green }} />
                  <Chip label={sectorLabel(pick.sector)} size="small" sx={{ fontWeight: 700, fontSize: '0.7rem', height: 24, bgcolor: alpha(C.indigo, 0.08), color: C.indigo }} />
                  {prob.confidence && (
                    <Chip label={prob.confidence} size="small" sx={{ fontWeight: 700, fontSize: '0.65rem', height: 22, bgcolor: alpha(confColor, 0.08), color: confColor }} />
                  )}
                </Box>

                {/* Trading Plan */}
                <Box sx={{ bgcolor: '#f8fafc', borderRadius: 2, border: '1px solid #e2e8f0', p: 2, mb: 2 }}>
                  <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#475569', mb: 1.5, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                    Kế hoạch giao dịch
                  </Typography>
                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 2 }}>
                    {[
                      ['Giá vào', fmtPrice(pick.entry_price), '#1e293b'],
                      ['Cắt lỗ', fmtPrice(pick.stop_loss_price), C.red],
                      ['Chốt lời', fmtPrice(pick.target_price), C.emerald],
                    ].map(([label, val, clr]) => (
                      <Box key={label}>
                        <Typography sx={{ fontSize: '0.62rem', color: '#94a3b8', fontWeight: 600, mb: 0.3 }}>{label}</Typography>
                        <Typography sx={{ fontSize: '0.95rem', fontWeight: 800, color: clr }}>{val}</Typography>
                      </Box>
                    ))}
                  </Box>
                  <Divider sx={{ my: 1.5 }} />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    {[
                      ['Lãi/Lỗ', pick.risk_reward, C.blue],
                      ['Chất lượng', pick.quality_score != null ? scoreLevel(pick.quality_score).text : '—', pick.quality_score != null ? scoreLevel(pick.quality_score).color : C.slate],
                      ['Xác suất thắng', prob.win_5d != null ? prob.win_5d + '%' : '—', confColor],
                      ['GTGD', fmtK(pick.value_traded), C.slate],
                    ].map(([label, val, clr]) => (
                      <Box key={label} sx={{ textAlign: 'center' }}>
                        <Typography sx={{ fontSize: '0.58rem', color: '#94a3b8', fontWeight: 600 }}>{label}</Typography>
                        <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: clr }}>{val}</Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>

                {/* Radar Chart */}
                <Box sx={{ bgcolor: '#fff', borderRadius: 2, border: '1px solid #e2e8f0', p: 2, mb: 2 }}>
                  <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#475569', mb: 1, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                    Đánh giá tổng quan
                  </Typography>
                  <Box sx={{ width: '100%', height: 200 }}>
                    <ResponsiveContainer>
                      <RadarChart data={radarData} outerRadius={70}>
                        <PolarGrid stroke="#e2e8f0" />
                        <PolarAngleAxis dataKey="subject" tick={{ fontSize: 11, fill: '#64748b' }} />
                        <PolarRadiusAxis domain={[0, 1]} tick={false} axisLine={false} />
                        <Radar dataKey="value" stroke={C.green} fill={C.green} fillOpacity={0.2} strokeWidth={2} />
                      </RadarChart>
                    </ResponsiveContainer>
                  </Box>
                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px 20px', mt: 1 }}>
                    {[
                      ['Xu hướng giá', scores.trend],
                      ['Sức mạnh tương đối', scores.relative_strength],
                      ['Thanh khoản', scores.volume],
                      ['Chống bẫy', scores.anti_trap],
                      ['Ngành hỗ trợ', scores.sector],
                      ['Phá vỡ', scores.breakout],
                      ['Điều chỉnh', scores.pullback],
                      ['Tích lũy ẩn', scores.stealth],
                    ].map(([label, val]) => {
                      const lvl = scoreLevel(val);
                      return (
                        <Box key={label} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <Typography sx={{ fontSize: '0.72rem', color: '#64748b' }}>{label}</Typography>
                          <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: lvl.color }}>
                            {lvl.text}
                          </Typography>
                        </Box>
                      );
                    })}
                  </Box>
                </Box>

                {/* Probability */}
                {(prob.expected_return_5d != null || prob.expected_return_10d != null) && (
                  <Box sx={{ bgcolor: '#f8fafc', borderRadius: 2, border: '1px solid #e2e8f0', p: 2 }}>
                    <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#475569', mb: 1, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                      Kỳ vọng lợi nhuận
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'space-around' }}>
                      {[
                        ['Sau 5 phiên', prob.expected_return_5d, prob.win_5d],
                        ['Sau 10 phiên', prob.expected_return_10d, prob.win_10d],
                      ].map(([label, ret, win]) => (
                        <Box key={label} sx={{ textAlign: 'center' }}>
                          <Typography sx={{ fontSize: '0.62rem', color: '#94a3b8', fontWeight: 600 }}>{label}</Typography>
                          <Typography sx={{ fontSize: '1rem', fontWeight: 800, color: (ret || 0) >= 0 ? C.green : C.red }}>
                            {fmtPct(ret)}
                          </Typography>
                          {win != null && (
                            <Typography sx={{ fontSize: '0.62rem', color: '#94a3b8' }}>Thắng: {win}%</Typography>
                          )}
                        </Box>
                      ))}
                      {prob.edge != null && (
                        <Box sx={{ textAlign: 'center' }}>
                          <Typography sx={{ fontSize: '0.62rem', color: '#94a3b8', fontWeight: 600 }}>Lợi thế</Typography>
                          <Typography sx={{ fontSize: '1rem', fontWeight: 800, color: prob.edge > 0 ? C.green : C.red }}>
                            {prob.edge}%
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </Box>
                )}
              </Box>
            </>
          );
        })()}
      </Drawer>
    </Box>
  );
}
